import { isEmpty, isNil, isUndefined } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { CardFormType } from '@shared/scenes/entity/cardFormTypeContext';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord } from '@shared/scenes/entity/types';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useViewTemplateLayout, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  isAuditCheckboxComponent,
  isAuditComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isLayoutComponent,
  isLayoutSection,
  isStaticTextComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { useSubformViewTemplate } from './useSubformViewTemplate';

export interface SubformData {
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  viewTemplate: API.ViewTemplateFragment | undefined;
  components: API.AnyComponent[];
  orderedComponents: API.AnyComponent[];
  fields: FieldRecord;
  updateField: (
    componentId: string,
    field: AuditTagField | AuditTextField | AuditCheckboxField | null,
  ) => void;
  errors: CardEntityError[];
  isValid: boolean;
  submitAttempted: boolean;
  setSubmitAttempted: Dispatch<SetStateAction<boolean>>;
}

export function useSubformData({
  workflowTemplateId,
  initialData,
  type,
  apiErrors,
}: {
  workflowTemplateId: string;
  initialData?: Record<string, SupportedSubformComponentValue>;
  type?: CardFormType;
  /** Server-side field-level errors to surface within the inner subform (e.g.
   *  COMPONENT_DATA_REQUIRED errors from the parent form save). Each error's
   *  componentId should match an inner subform component. */
  apiErrors?: CardEntityError[];
}): SubformData {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const components = useMemo(
    () => workflowTemplate?.components.filter(isSubformWorkflowComponent) ?? [],
    [workflowTemplate?.components],
  );

  const [fields, setFields] = useState<FieldRecord>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Initialize (and reset) fields whenever workflowTemplateId changes, waiting for
  // components to finish loading. initialData is captured at that moment.
  const lastInitializedId = useRef('');
  useEffect(() => {
    if (
      !workflowTemplateId ||
      workflowTemplateId === lastInitializedId.current ||
      isEmpty(components)
    ) {
      return;
    }

    lastInitializedId.current = workflowTemplateId;
    setFields(initFieldRecord(components, initialData));
    setSubmitAttempted(false);
    // initialData intentionally omitted — we only want to capture it at init time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowTemplateId, components]);

  const viewTemplate = useSubformViewTemplate({
    workflowTemplateId,
    type: type ?? 'EDIT',
  });
  const { layout } = useViewTemplateLayout(workflowTemplateId, viewTemplate?.id);

  // Use the same hook chain as useEditCardEntity: useVisibleComponents (filters isHidden) →
  // applyComponentOverrides (merges required/readonly/isHidden overrides onto components).
  const allViewComponents = useViewComponents({ workflowTemplate, viewTemplate });
  const effectiveComponents = useMemo(
    () => allViewComponents.filter(isSubformWorkflowComponent),
    [allViewComponents],
  );

  const { errors, isValid } = useFormErrors({
    fields,
    components: effectiveComponents,
    apiErrors,
    includeOnSubmitErrors: submitAttempted,
    includeLocalErrors: true,
  });

  const updateField = useCallback(
    (componentId: string, field: AuditTagField | AuditTextField | AuditCheckboxField | null) => {
      setFields(prev => ({ ...prev, [componentId]: field }));
    },
    [],
  );

  const orderedComponents = useMemo(
    () => applyLayoutOrder(effectiveComponents, layout),
    [effectiveComponents, layout],
  );

  return {
    workflowTemplate,
    viewTemplate,
    components: effectiveComponents,
    orderedComponents,
    fields,
    updateField,
    errors,
    isValid,
    submitAttempted,
    setSubmitAttempted,
  };
}

function isSubformWorkflowComponent(component: API.AnyComponent): boolean {
  return isAuditComponent(component) || isStaticTextComponent(component);
}

function applyLayoutOrder(
  components: API.AnyComponent[],
  layout?: API.ViewTemplateLayoutFragment | null,
): API.AnyComponent[] {
  if (isNil(layout)) {
    return components;
  }

  const componentMap = new Map(components.map(c => [c.id, c]));
  const ordered: API.AnyComponent[] = [];
  const seen = new Set<string>();

  for (const item of layout.items ?? []) {
    if (isLayoutComponent(item) && item.componentId) {
      const component = componentMap.get(item.componentId);
      if (component && !seen.has(component.id)) {
        ordered.push(component);
        seen.add(component.id);
      }
    } else if (isLayoutSection(item)) {
      for (const componentId of item.componentIds ?? []) {
        const component = componentMap.get(componentId);
        if (component && !seen.has(component.id)) {
          ordered.push(component);
          seen.add(component.id);
        }
      }
    }
  }

  for (const component of components) {
    if (!seen.has(component.id)) {
      ordered.push(component);
    }
  }

  return ordered;
}

function initFieldRecord(
  components: API.AnyComponent[],
  initialData?: Record<string, SupportedSubformComponentValue>,
): FieldRecord {
  const record: FieldRecord = {};
  for (const component of components) {
    const raw = applyDefaultValue(component, initialData?.[component.id]);
    if (isAuditTagComponent(component)) {
      record[component.id] = AuditTagField.fromFieldJson(raw);
    } else if (isAuditTextComponent(component)) {
      record[component.id] = AuditTextField.fromFieldJson(raw);
    } else if (isAuditCheckboxComponent(component)) {
      record[component.id] = AuditCheckboxField.fromFieldJson(raw);
    }
  }
  return record;
}

function applyDefaultValue(
  component: API.AnyComponent,
  initialDataValue?: SupportedSubformComponentValue,
): SupportedSubformComponentValue | undefined {
  if (isTextComponent(component) && isUndefined(initialDataValue)) {
    return component.default?.text;
  }
  return initialDataValue;
}
